import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';

interface IProps {
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  width?: FieldWidth;
  autoFocus?: boolean;
  onFocus?: FieldEvent<string>;
  onChange?: FieldEvent<string>;
  onBlur?: FieldEvent<string>;
}

function FormPasswordField({
  name,
  label,
  tooltip,
  description,
  placeholder,
  disabled,
  width,
  onFocus,
  onChange,
  onBlur,
  autoFocus = false,
}: IProps) {
  const ref = useRef(null);
  const isInitialMount = useRef(true);

  const {
    id,
    fieldProps,
    inputProps,
    required,
    disabled: formDisabled,
  } = useFormField<string>(name, width, { onFocus, onChange, onBlur }, autoFocus);
  const [isShowingPassword, setShowingPassword] = useState(false);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      const length = inputProps.value.length;
      ref.current.focus();
      ref.current.setSelectionRange(length, length);
    }
  }, [isShowingPassword]);

  const isDisabled = formDisabled || disabled;
  const className = classNames('novoInputWithButton', { 'novoInputWithButton--disabled': isDisabled });
  return (
    <div {...fieldProps}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <div className={className}>
        <input
          ref={ref}
          className="novoFormField__input"
          id={id}
          type={isShowingPassword ? 'text' : 'password'}
          placeholder={placeholder}
          {...inputProps}
          disabled={isDisabled}
          value={inputProps.value || ''}
          required={required}
        />
        <button
          type="button"
          className="material-icons novoInputWithButton__button"
          onClick={() => setShowingPassword(!isShowingPassword)}
          disabled={disabled}
          tabIndex={-1}
        >
          {isShowingPassword ? 'remove_red_eye' : 'visibility_off'}
        </button>
      </div>
      <FormFieldError name={name} />
    </div>
  );
}

export default FormPasswordField;
